import React from 'react';
import { BottomNavigation, BottomNavigationAction, Paper } from '@mui/material';
import { Home, People, CalendarToday, Person } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

interface BottomNavProps {
  counsellorId?: string;
}

const BottomNav: React.FC<BottomNavProps> = ({ counsellorId }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const getActiveTab = () => {
    // Calendar
    if (location.pathname.includes('/calendar')) return 2;
    // Profile
    if (location.pathname.includes('/counsellors/')) return 3;
    // Client details (from dashboard)
    if (location.pathname.includes('/client-details/')) return 1;
    // Dashboard - default for counsellors
    if (location.pathname === '/counsellor/dashboard') return 0;
    // Admin clients page
    if (location.pathname === '/' && location.search.includes('tab=clients')) return 1;
    if (location.pathname === '/') return 1;
    // Default to dashboard
    return 0;
  };

  const [value, setValue] = React.useState(getActiveTab());

  React.useEffect(() => {
    setValue(getActiveTab());
  }, [location.pathname, location.search]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    switch (newValue) {
      case 0:
        // Dashboard - scroll to top
        navigate('/counsellor/dashboard');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;
      case 1:
        // Clients - navigate to dashboard and scroll to clients section
        if (location.pathname === '/counsellor/dashboard') {
          // Already on dashboard, scroll to clients
          const clientsSection = document.getElementById('clients-section');
          if (clientsSection) {
            clientsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        } else {
          // Navigate to dashboard first, then scroll
          navigate('/counsellor/dashboard');
          setTimeout(() => {
            const clientsSection = document.getElementById('clients-section');
            if (clientsSection) {
              clientsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }
        break;
      case 2:
        navigate('/calendar');
        break;
      case 3:
        if (counsellorId) {
          navigate(`/counsellors/${counsellorId}`);
        }
        break;
    }
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        display: { xs: 'block', md: 'none' },
        zIndex: 1000,
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)'
      }}
      elevation={3}
    >
      <BottomNavigation
        value={value}
        onChange={handleChange}
        sx={{
          background: 'transparent',
          height: 64,
          '& .MuiBottomNavigationAction-root': {
            color: 'rgba(255, 255, 255, 0.5)',
            minWidth: 'auto',
            padding: '6px 12px',
            '&.Mui-selected': {
              color: '#667eea',
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.75rem',
                fontWeight: 600
              }
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              marginTop: '4px'
            }
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1.5rem'
          }
        }}
      >
        <BottomNavigationAction
          label="Dashboard"
          icon={<Home />}
          sx={{
            '&.Mui-selected': {
              '& .MuiSvgIcon-root': {
                color: '#667eea'
              }
            }
          }}
        />
        <BottomNavigationAction
          label="Clients"
          icon={<People />}
          sx={{
            '&.Mui-selected': {
              '& .MuiSvgIcon-root': {
                color: '#667eea'
              }
            }
          }}
        />
        <BottomNavigationAction
          label="Calendar"
          icon={<CalendarToday />}
          sx={{
            '&.Mui-selected': {
              '& .MuiSvgIcon-root': {
                color: '#667eea'
              }
            }
          }}
        />
        <BottomNavigationAction
          label="Profile"
          icon={<Person />}
          disabled={!counsellorId}
          sx={{
            '&.Mui-selected': {
              '& .MuiSvgIcon-root': {
                color: '#667eea'
              }
            }
          }}
        />
      </BottomNavigation>
    </Paper>
  );
};

export default BottomNav;
